import { api } from './api.js';

/**
 * Frontend helpers for the browser Web Push API. The service worker lives at
 * /sw.js. These calls coordinate the three players: the browser's
 * PushManager, the backend's stored subscription list, and the user.
 */

export type PushState = 'unsupported' | 'idle' | 'denied' | 'subscribed';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ?? navigator.serviceWorker.register('/sw.js');
}

export async function getCurrentState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (sub) return 'subscribed';
  } catch { /* ignore */ }
  return 'idle';
}

export async function subscribe(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'idle';

  const { public_key } = await api.get<{ public_key: string }>('/api/push/vapid');
  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key) as BufferSource,
    });
  }
  const json = sub.toJSON();
  await api.post('/api/push/subscribe', {
    endpoint: json.endpoint,
    keys: json.keys,
    ua: navigator.userAgent,
  });
  return 'subscribed';
}

export async function unsubscribe(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    const endpoint = sub.endpoint;
    await sub.unsubscribe().catch(() => {});
    await api.post('/api/push/unsubscribe', { endpoint }).catch(() => {});
  }
  return 'idle';
}
